import { Component, Inject, Input, LOCALE_ID, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS,
  PasswordRequirementId,
} from '../../utilities/passwordRequirements';
import { resolveActiveLocale } from '../../utilities/localeDirection';

interface PasswordRequirementView {
  id: PasswordRequirementId;
  label: string;
  met: boolean;
  ariaLabel: string;
}

@Component({
  selector: 'app-password-requirements',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './password-requirements.component.html',
  styleUrls: ['./password-requirements.component.scss'],
})
export class PasswordRequirementsComponent implements OnChanges {
  @Input() password: string | null = '';

  requirements: PasswordRequirementView[] = [];

  private readonly labels: Record<PasswordRequirementId, string>;

  constructor(@Inject(LOCALE_ID) private locale: string) {
    const minLength = new Intl.NumberFormat(resolveActiveLocale(this.locale), {
      maximumFractionDigits: 0,
    }).format(PASSWORD_MIN_LENGTH);

    this.labels = {
      minLength: $localize`:@@passwordRequirements.minLength:Minimum of ${minLength}:count: characters`,
      uppercase: $localize`:@@passwordRequirements.uppercase:At least 1 uppercase letter`,
      lowercase: $localize`:@@passwordRequirements.lowercase:At least 1 lowercase letter`,
      number: $localize`:@@passwordRequirements.number:At least 1 number`,
      symbol: $localize`:@@passwordRequirements.symbol:At least 1 special character`,
    };

    this.requirements = this.buildRequirements();
  }

  ngOnChanges(): void {
    this.requirements = this.buildRequirements();
  }

  trackByRequirement(_index: number, requirement: PasswordRequirementView): string {
    return requirement.id;
  }

  private buildRequirements(): PasswordRequirementView[] {
    const password = this.password ?? '';
    const met = $localize`:@@passwordRequirements.met:requirement met`;
    const notMet = $localize`:@@passwordRequirements.notMet:requirement not met`;

    return PASSWORD_REQUIREMENTS.map((requirement) => {
      const isMet = requirement.test(password);
      const label = this.labels[requirement.id];

      return {
        id: requirement.id,
        met: isMet,
        label,
        ariaLabel: `${label} - ${isMet ? met : notMet}`,
      };
    });
  }
}
